import { z } from 'zod';

/**
 * The identity of a notification template — `<domain>.<area>.<name>`.
 *
 * Dot-separated lower-kebab segments, matching the keys P20a already emits
 * (`moderation.notice.legal-consequences`) and the per-type family it builds
 * (`moderation.violation.<violation_type_key>`). A violation type key is
 * `lower_snake_case`, so the last segment must accept underscores too — a
 * pattern that rejected them would have made every per-type notice unroutable.
 *
 * 120 characters because `violation_types.legal_notice_template_key` is
 * `varchar(120)`: the two must agree or a key that saves in one place is refused
 * in the other.
 */
export const NOTIFICATION_TEMPLATE_KEY_PATTERN =
  /^[a-z][a-z0-9]*(?:[-_][a-z0-9]+)*(?:\.[a-z][a-z0-9]*(?:[-_][a-z0-9]+)*)+$/;

export const NOTIFICATION_TEMPLATE_KEY_MAX_LENGTH = 120;

export const NotificationTemplateKeySchema = z
  .string()
  .trim()
  .min(3)
  .max(NOTIFICATION_TEMPLATE_KEY_MAX_LENGTH)
  .regex(
    NOTIFICATION_TEMPLATE_KEY_PATTERN,
    'A template key is dot-separated lower-case segments, e.g. "matching.match.created".',
  );

export type NotificationTemplateKey = z.infer<typeof NotificationTemplateKeySchema>;

/**
 * The placeholder grammar the renderer interpolates: `{{name}}`.
 *
 * Whitespace inside the braces is tolerated because an operator typing into a
 * two-locale editor will produce `{{ name }}` sooner or later, and a notice that
 * silently kept the braces would be worse than one that trimmed them.
 */
const PLACEHOLDER_PATTERN = /\{\{\s*([a-zA-Z][a-zA-Z0-9]*)\s*\}\}/g;

/**
 * Every distinct placeholder in a piece of template text, in first-seen order.
 *
 * Used in two places that must agree: the admin save path refuses a template
 * naming a parameter the catalogue does not supply, and the renderer refuses at
 * delivery time when a required one arrives empty. Extracting rather than
 * splitting on the brace characters means a stray `{` in prose is prose.
 */
export function templatePlaceholders(text: string): readonly string[] {
  const found: string[] = [];

  for (const match of text.matchAll(PLACEHOLDER_PATTERN)) {
    const name = match[1];
    if (name !== undefined && !found.includes(name)) {
      found.push(name);
    }
  }

  return found;
}

/**
 * Replaces every placeholder using `lookup`.
 *
 * A `lookup` returning `undefined` leaves the placeholder **in place** rather
 * than blanking it — the caller is then able to tell that something was not
 * filled and refuse the whole render (N17: a notice missing its subject is worse
 * than no notice). Blanking here would make the omission invisible downstream.
 */
export function interpolateTemplate(
  text: string,
  lookup: (name: string) => string | undefined,
): string {
  return text.replace(PLACEHOLDER_PATTERN, (whole, name: string) => lookup(name) ?? whole);
}
